import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../services/AxiosInstance';
import { accessControlService } from '../../services/accessControlService';
import { getRoleBasedUserFilters } from '../../utils/roleAccess';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import './commonstyle.css';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import '../users/userform.css';
import './flowdoRole.css';
import { applyLinkedSectionView, linkDocumentClassificationView, withClassificationActions } from '../../utils/classificationAccess';

const ACCESS_MODULE_GROUPS = [
  {
    id: 'tasks',
    title: 'Tasks',
    icon: 'fas fa-tasks',
    keys: ['tasks', 'worksheet', 'performance', 'projects', 'work_register', 'work_register_team'],
  },
  {
    id: 'snap_bill',
    title: 'Snap Bill',
    icon: 'fas fa-file-invoice',
    keys: [
      'invoice_scanner_access',
      'invoice_scanner_invoices',
      'invoice_scanner_quotations',
      'invoice_scanner_soa',
      'invoice_scanner_settings',
      'invoice_scanner_vendors',
      'invoice_scanner_customers',
      'invoice_scanner_projects',
      'invoice_scanner_accounts',
    ],
  },
  {
    id: 'collaboration',
    title: 'Collaboration',
    icon: 'fas fa-comments',
    keys: ['forum', 'documents', 'doc_classifications'],
  },
  {
    id: 'organization',
    title: 'Organization',
    icon: 'fas fa-building',
    keys: ['users', 'staff', 'settings', 'vehicles', 'credentials'],
  },
];

const ACCESS_ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'create_edit', label: 'Create / Edit' },
  { key: 'delete', label: 'Delete' },
  { key: 'export', label: 'Export' },
  { key: 'share', label: 'Share' },
  { key: 'download', label: 'Download' },
];

const MODULE_ICONS = {
  tasks: 'fas fa-tasks',
  worksheet: 'fas fa-table',
  performance: 'fas fa-chart-line',
  projects: 'fas fa-briefcase',
  work_register: 'fas fa-clipboard-list',
  work_register_team: 'fas fa-users',
  forum: 'fas fa-comments',
  documents: 'fas fa-file-alt',
  doc_classifications: 'fas fa-tags',
  users: 'fas fa-users',
  staff: 'fas fa-user-tie',
  settings: 'fas fa-cog',
  vehicles: 'fas fa-car',
  credentials: 'fas fa-key',
  invoice_scanner_access: 'fas fa-external-link-alt',
  invoice_scanner_invoices: 'fas fa-file-invoice',
  invoice_scanner_quotations: 'fas fa-file-signature',
  invoice_scanner_soa: 'fas fa-file-invoice-dollar',
  invoice_scanner_settings: 'fas fa-cog',
  invoice_scanner_vendors: 'fas fa-truck',
  invoice_scanner_customers: 'fas fa-user-friends',
  invoice_scanner_projects: 'fas fa-project-diagram',
  invoice_scanner_accounts: 'fas fa-sitemap',
};

const sectionAllowsAction = (section, actionKey) =>
  (section?.actions || []).some((action) => action.key === actionKey);

const sectionDisplayName = (section) =>
  section?.key === 'invoice_scanner_access' ? 'Snap Bill' : section?.name;

function SelectAllCheckbox({ checked, indeterminate, disabled, onChange, label }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label className={`flowdo-role-select-all ${checked ? 'is-checked' : ''}`}>
      <input
        ref={inputRef}
        className="form-check-input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={label}
      />
      <span>Select all</span>
    </label>
  );
}

const groupCatalogByModule = (catalog) => {
  const used = new Set();
  const groups = ACCESS_MODULE_GROUPS.map((group) => {
    const sections = group.keys
      .map((key) => (catalog || []).find((section) => section.key === key))
      .filter(Boolean);
    sections.forEach((section) => used.add(section.key));
    const actions = ACCESS_ACTIONS.filter((action) =>
      sections.some((section) => sectionAllowsAction(section, action.key))
    );
    return { ...group, sections, actions };
  }).filter((group) => group.sections.length);
  const leftover = (catalog || []).filter((section) => !used.has(section.key));
  if (leftover.length) {
    groups.push({
      id: 'other',
      title: 'Other',
      icon: 'fas fa-th-large',
      sections: leftover,
      actions: ACCESS_ACTIONS.filter((action) =>
        leftover.some((section) => sectionAllowsAction(section, action.key))
      ),
    });
  }
  return groups;
};

const emptySelection = (sections) => {
  const next = {};
  (sections || []).forEach((section) => {
    next[section.key] = {};
    section.actions.forEach((action) => {
      next[section.key][action.key] = false;
    });
  });
  return next;
};

const selectionFromAccess = (catalog, access) => {
  const next = emptySelection(catalog);
  (access || []).forEach((section) => {
    if (!next[section.section_key]) next[section.section_key] = {};
    (section.actions || []).forEach((actionKey) => {
      next[section.section_key][actionKey] = true;
    });
  });
  return next;
};

const AccessControl = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const moduleGroups = useMemo(() => groupCatalogByModule(catalog), [catalog]);

  const notify = (msg, isError = false) => {
    (isError ? toast.error : toast.success)(msg, {
      position: 'top-right',
      autoClose: 5000,
    });
  };

  const fetchUsers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      const filters = await getRoleBasedUserFilters(axiosInstance);
      const params = new URLSearchParams();
      params.append('corporate_id', corporateId);
      if (filters.branch) params.append('branch', filters.branch);
      if (filters.department) params.append('department', filters.department);
      if (filters.sub_department) params.append('sub_department', filters.sub_department);
      params.append('limit', 500);
      const res = await axiosInstance.get(`/list/corporateuserslist?${params}`);
      const list = res.data?.users || res.data?.data || [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Fetch users error:', err);
      notify('Failed to load users', true);
    }
  };

  useEffect(() => {
    fetchUsers();
    accessControlService.getActionCatalog()
      .then((res) => {
        const sections = withClassificationActions(res?.data?.data?.sections || []);
        setCatalog(sections);
        setSelected(emptySelection(sections));
      })
      .catch((err) => {
        console.error('Fetch action catalog error:', err);
        notify('Failed to load access options', true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const userId = location.state?.userId;
    if (userId && users.length > 0) {
      setSelectedUserId(String(userId));
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state?.userId, users.length, location.pathname]);

  useEffect(() => {
    if (!selectedUserId || !catalog.length) {
      if (!selectedUserId) setSelected(emptySelection(catalog));
      setLoadingUser(false);
      return;
    }
    setLoadingUser(true);
    accessControlService
      .getUserAccess(selectedUserId)
      .then((res) => {
        const sections = res?.data?.data?.sections || [];
        setSelected(linkDocumentClassificationView(selectionFromAccess(catalog, sections)));
      })
      .catch((err) => {
        console.error('Fetch user access error:', err);
        notify('Failed to load user access', true);
      })
      .finally(() => setLoadingUser(false));
  }, [selectedUserId, catalog]);

  const groupChecks = (group) => {
    let total = 0;
    let checkedCount = 0;
    group.sections.forEach((section) => {
      (section.actions || []).forEach((action) => {
        total += 1;
        if (selected[section.key]?.[action.key]) checkedCount += 1;
      });
    });
    return {
      all: total > 0 && checkedCount === total,
      partial: checkedCount > 0 && checkedCount < total,
    };
  };

  const allChecks = () => {
    let total = 0;
    let checkedCount = 0;
    catalog.forEach((section) => {
      (section.actions || []).forEach((action) => {
        total += 1;
        if (selected[section.key]?.[action.key]) checkedCount += 1;
      });
    });
    return {
      all: total > 0 && checkedCount === total,
      partial: checkedCount > 0 && checkedCount < total,
    };
  };

  const toggleGroup = (group, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      group.sections.forEach((section) => {
        const row = { ...(next[section.key] || {}) };
        (section.actions || []).forEach((action) => {
          row[action.key] = checked;
        });
        next[section.key] = row;
      });
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      catalog.forEach((section) => {
        const row = { ...(next[section.key] || {}) };
        (section.actions || []).forEach((action) => {
          row[action.key] = checked;
        });
        next[section.key] = row;
      });
      return next;
    });
  };

  const toggleAction = (sectionKey, actionKey, checked) => {
    setSelected((prev) => applyLinkedSectionView(prev, sectionKey, actionKey, checked));
  };

  const buildSections = () => catalog.map((section) => ({
    section_key: section.key,
    actions: section.actions
      .map((action) => action.key)
      .filter((actionKey) => selected[section.key]?.[actionKey]),
  })).filter((section) => section.actions.length > 0);

  const handleSave = async () => {
    if (!selectedUserId) {
      notify('Please select a user', true);
      return;
    }
    setSaving(true);
    try {
      await accessControlService.saveUserAccess(selectedUserId, {
        sections: buildSections(),
        fullAccess: allChecks().all || undefined,
      });
      notify('Access saved successfully');
      window.dispatchEvent(new Event('menu-access-updated'));
      if (location.state?.fromUserList) {
        navigate('/settings/userlist');
      }
    } catch (err) {
      console.error('Save access error:', err);
      notify(err?.response?.data?.error || 'Failed to save access', true);
    } finally {
      setSaving(false);
    }
  };

  const fullSelection = allChecks();

  return (
    <>
      <Row style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <Col lg={12}>
          <Card className="user-list-card access-control-card flowdo-role-page">
            <Card.Body className="access-control-card-body">
              <div className="filter-group mb-4 user-form-select-container" style={{ maxWidth: '400px' }}>
                <label className="text-label">Select User</label>
                <Select
                  classNamePrefix="user-form-select"
                  options={users.map((u) => {
                    const id = u.CorporateUserId || u.id;
                    const name = u.name || u.full_name || `User ${id}`;
                    const email = u.email || u.User?.email || '';
                    return {
                      value: String(id),
                      label: email ? `${name} (${email})` : name,
                    };
                  })}
                  value={selectedUserId
                    ? (() => {
                        const u = users.find((user) => String(user.CorporateUserId || user.id) === selectedUserId);
                        const name = u?.name || u?.full_name || `User ${selectedUserId}`;
                        const email = u?.email || u?.User?.email || '';
                        return { value: selectedUserId, label: email ? `${name} (${email})` : name };
                      })()
                    : null}
                  onChange={(opt) => setSelectedUserId(opt?.value || '')}
                  isSearchable
                  isClearable
                  placeholder="-- Select User --"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              {selectedUserId && (
                <>
                  {loading || loadingUser ? (
                    <LoadingSpinner />
                  ) : (
                    <section className="flowdo-role-panel">
                      <header className="flowdo-role-panel__head">
                        <h3>User Access Control</h3>
                        <p>View is selected automatically when any other option is selected. Documents and Classifications view stay selected together. A page the user already had includes every action on that page.</p>
                        <SelectAllCheckbox
                          checked={fullSelection.all}
                          indeterminate={fullSelection.partial}
                          label="Full access to all actions"
                          onChange={(event) => toggleAll(event.target.checked)}
                        />
                      </header>
                      <div className="flowdo-role-panel__body">
                        <div className="flowdo-role-access-modules">
                          {moduleGroups.map((group) => {
                            const selection = groupChecks(group);
                            return (
                              <div className="flowdo-role-module-group" key={group.id}>
                                <h4 className="flowdo-role-module-group__title">
                                  <i className={group.icon} aria-hidden="true" />
                                  {group.title}
                                  <SelectAllCheckbox
                                    checked={selection.all}
                                    indeterminate={selection.partial}
                                    label={`Select all ${group.title} permissions`}
                                    onChange={(event) => toggleGroup(group, event.target.checked)}
                                  />
                                </h4>
                                <div className="table-responsive">
                                  <table className="flowdo-role-access-table">
                                    <thead>
                                      <tr>
                                        <th>Module</th>
                                        {group.actions.map((action) => (
                                          <th key={action.key}>{action.label}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.sections.map((section) => (
                                        <tr key={section.key}>
                                          <td>
                                            <span className="flowdo-role-access__name">
                                              <i
                                                className={MODULE_ICONS[section.key] || 'fas fa-cube'}
                                                aria-hidden="true"
                                              />
                                              {sectionDisplayName(section)}
                                            </span>
                                          </td>
                                          {group.actions.map((action) => {
                                            const applicable = sectionAllowsAction(section, action.key);
                                            const checked = applicable && !!selected[section.key]?.[action.key];
                                            return (
                                              <td key={action.key} className="flowdo-role-access-table__check">
                                                <label className={`flowdo-role-check ${checked ? 'is-checked' : ''} ${!applicable ? 'is-na' : ''}`}>
                                                  <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={!applicable}
                                                    onChange={(e) => {
                                                      if (!applicable) return;
                                                      toggleAction(section.key, action.key, e.target.checked);
                                                    }}
                                                    aria-label={`${sectionDisplayName(section)} ${action.label}`}
                                                  />
                                                </label>
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </section>
                  )}

                  <div className="text-end toolbar toolbar-bottom p-2 flowdo-role-toolbar">
                    <button
                      type="button"
                      className="new-user-btn"
                      onClick={handleSave}
                      disabled={saving || loadingUser}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <BackToTop />
      <InternalFooter />
    </>
  );
};

export default AccessControl;
